import type { LanguageCode, MultilingualText } from './types';
import { isValidLanguageCode } from './types';

/**
 * Parse a param value into a MultilingualText object.
 * Handles both old (plain text) and new (JSON) formats.
 *
 * @param value - The param value (can be plain string or JSON string)
 * @param defaultLanguage - Language to use when migrating plain text
 * @returns Parsed multilingual text object
 *
 * @example
 * // Old format (plain text)
 * parseMultilingualText("Hello world") // { en: "Hello world" }
 *
 * // New format (JSON)
 * parseMultilingualText('{"en":"Hello","es":"Hola"}') // { en: "Hello", es: "Hola" }
 */
export const parseMultilingualText = (
  value: string | null | undefined,
  defaultLanguage: LanguageCode = 'en-US'
): MultilingualText => {
  if (!value || typeof value !== 'string') {
    return { [defaultLanguage]: '' };
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return { [defaultLanguage]: '' };
  }

  // Try to parse as JSON
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        // Validate and filter to only valid language codes
        const result: MultilingualText = {};
        for (const [key, val] of Object.entries(parsed)) {
          if (isValidLanguageCode(key) && typeof val === 'string') {
            result[key] = val;
          }
        }
        return Object.keys(result).length > 0
          ? result
          : { [defaultLanguage]: '' };
      }
    } catch {
      // Not valid JSON, treat as plain text
    }
  }

  // Plain text - migrate to default language
  return { [defaultLanguage]: value };
};

/**
 * Serialize a MultilingualText object to JSON string.
 *
 * @param text - The multilingual text object
 * @returns JSON string representation
 */
export const serializeMultilingualText = (text: MultilingualText): string => {
  return JSON.stringify(text);
};

/**
 * Get text for a specific language from a multilingual text object.
 * Falls back to English, then first available language.
 *
 * @param text - The multilingual text object
 * @param language - Preferred language code
 * @returns Text for the language or empty string
 */
export const getTextForLanguage = (
  text: MultilingualText,
  language: LanguageCode
): string => {
  if (language in text) {
    return text[language] ?? '';
  }

  // Fallback to first available language
  const firstLang = Object.keys(text)[0] as LanguageCode | undefined;
  return firstLang ? (text[firstLang] ?? '') : '';
};

/**
 * Get display text from a param value.
 * Convenience function for read-only display scenarios.
 *
 * @param paramValue - The param value (can be plain string or JSON string)
 * @param preferredLanguage - Preferred language code
 * @returns Text for display
 */
export const getDisplayText = (
  paramValue: string | null | undefined,
  preferredLanguage: LanguageCode = 'en-US'
): string => {
  const parsed = parseMultilingualText(paramValue, preferredLanguage);
  return getTextForLanguage(parsed, preferredLanguage);
};

/**
 * Get available languages from a multilingual text object.
 *
 * @param text - The multilingual text object
 * @param defaultLanguage - Default language if none found
 * @returns Array of language codes
 */
export const getAvailableLanguages = (
  text: MultilingualText,
  defaultLanguage: LanguageCode = 'en-US'
): LanguageCode[] => {
  const languages = Object.keys(text).filter(isValidLanguageCode);
  return languages.length > 0 ? languages : [defaultLanguage];
};

/**
 * Update text for a specific language.
 *
 * @param text - The current multilingual text
 * @param language - Language to update
 * @param value - New text value
 * @returns Updated multilingual text
 */
export const updateTextForLanguage = (
  text: MultilingualText,
  language: LanguageCode,
  value: string
): MultilingualText => ({
  ...text,
  [language]: value,
});

/**
 * Add or remove languages from a multilingual text object.
 *
 * @param text - The current multilingual text
 * @param languages - Languages to keep
 * @returns Updated multilingual text with only specified languages
 */
export const updateAvailableLanguages = (
  text: MultilingualText,
  languages: LanguageCode[]
): MultilingualText => {
  const result: MultilingualText = {};

  // Keep text for languages that are still selected
  for (const lang of languages) {
    result[lang] = text[lang] ?? '';
  }

  return result;
};

/**
 * Validate multilingual text has at least one non-empty language.
 *
 * @param text - The multilingual text object
 * @returns True if at least one language has text
 */
export const hasContent = (text: MultilingualText): boolean => {
  return Object.values(text).some((value) => value && value.trim().length > 0);
};

/**
 * Validate all language codes in a multilingual text object are valid.
 *
 * @param text - The multilingual text object
 * @returns True if all language codes are valid
 */
export const hasValidLanguageCodes = (text: MultilingualText): boolean => {
  return Object.keys(text).every(isValidLanguageCode);
};
